import { Injectable } from '@angular/core';
import {
  AlignmentType,
  BorderStyle,
  Document,
  Header,
  HeadingLevel,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TabStopType,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';

import { PickListService } from '../../../../core/shared/services/picklist.service';
import {
  CreditTermParamsDto,
  FacilityOfferLetterParamsDto,
  OfferLetterCovenant,
} from '../models/credit-proposal-letter.model';

/* ====================================================== */
/* DOCUMENT CONSTANTS                                     */
/* ====================================================== */

const BLACK = '000000';
const FONT_SIZE = 21; // 10.5pt — matches the reference letter's body text.
const TABLE_FONT_SIZE = 20; // 10pt — term sheet table cells.

const THIN_BORDER = { style: BorderStyle.SINGLE, size: 4, color: BLACK };

/**
 * Term sheet column widths in twips (DXA), fixed so Word never re-flows them
 * to the content: a long value (e.g. a big amount) used to squeeze the two
 * left columns. Together they fill an A4 page's text width with the default
 * 1-inch margins (11906 - 2 x 1440 = 9026). Number 5% / label 20% / value 75%.
 */
const TERM_SHEET_COLUMN_WIDTHS = [450, 1805, 6771];

const TABLE_BORDERS = {
  top: THIN_BORDER,
  bottom: THIN_BORDER,
  left: THIN_BORDER,
  right: THIN_BORDER,
  insideHorizontal: THIN_BORDER,
  insideVertical: THIN_BORDER,
};

/**
 * REPATMENTS_TERMS (picklist group 34) ref codes used by `crdtTrmParamsLst[].typ`.
 * Rows are bucketed into "Term Finance" vs "Islamic Finance" by matching the
 * resolved picklist label — this is the same loadCodesAsync/getOptionsSync
 * pattern `terms-parameters.ts` already uses for the same `typ` field.
 */
const REPATMENTS_TERMS_GROUP = 34;
const DISBURSEMENT_FREQUENCY_GROUP = 10;
const KIBOR_RESET_PERIOD_GROUP = 14;
/** FACILITY_TYPE — resolves `trmRpymntLst[].typ` to the facility name shown under Tenor. */
const FACILITY_TYPE_GROUP = 13;

/** Header logo — natural size is 690x977px; scaled down, aspect ratio preserved. */
const LOGO_PATH = 'images/fol-logo.png';
const LOGO_WIDTH_PX = 55;
const LOGO_HEIGHT_PX = Math.round((LOGO_WIDTH_PX * 977) / 690);

@Injectable({ providedIn: 'root' })
export class FacilityOfferLetterExportService {
  constructor(private pickListService: PickListService) {}

  /**
   * Loads the picklist groups used to resolve `typ` / `rpymntFrqncy` /
   * `kbrRstPrd` labels, then builds and downloads the offer letter .docx.
   */
  async exportToWord(
    foParams: FacilityOfferLetterParamsDto,
    trmParams: CreditTermParamsDto,
    covenants: OfferLetterCovenant[],
    outcomes: string[],
    fileName = 'Facility-Offer-Letter',
  ): Promise<void> {
    await new Promise<void>((resolve) => {
      this.pickListService
        .loadCodesAsync([
          REPATMENTS_TERMS_GROUP,
          DISBURSEMENT_FREQUENCY_GROUP,
          KIBOR_RESET_PERIOD_GROUP,
          FACILITY_TYPE_GROUP,
        ])
        .subscribe({
          next: () => resolve(),
          error: () => resolve(),
        });
    });

    const values = this.buildDynamicValues(foParams, trmParams, covenants, outcomes);
    const header = await this.buildHeader();

    const doc = new Document({
      styles: {
        default: {
          document: {
            run: { font: 'Calibri', size: FONT_SIZE, color: BLACK },
            paragraph: { spacing: { after: 90, line: 240 } },
          },
        },
      },
      sections: [
        {
          properties: {},
          headers: header ? { default: header } : undefined,
          children: [
            ...this.buildOfferLetterBody(values),
            ...this.buildTermSheet(values),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${this.safeFileName(fileName)}.docx`);
  }

  /**
   * Fetches the header logo from `public/images/fol-logo.png` and wraps it in
   * a right-aligned Header so it repeats top-right on every page. Returns
   * null (no header) if the asset can't be loaded, so a missing/broken image
   * never breaks document generation.
   */
  private async buildHeader(): Promise<Header | null> {
    // TEMPORARILY DISABLED — logo hidden on request; re-enable by restoring the block below.
    return null;

    /*
    try {
      const response = await fetch(LOGO_PATH);
      if (!response.ok) throw new Error(`Logo fetch failed: ${response.status}`);

      const logoBytes = await response.arrayBuffer();

      return new Header({
        children: [
          new Paragraph({
            alignment: AlignmentType.RIGHT,
            spacing: { after: 0 },
            children: [
              new ImageRun({
                type: 'png',
                data: logoBytes,
                transformation: { width: LOGO_WIDTH_PX, height: LOGO_HEIGHT_PX },
              }),
            ],
          }),
        ],
      });
    } catch (error) {
      console.error('Unable to load offer letter header logo:', error);
      return null;
    }
    */
  }

  /* ====================================================== */
  /* DYNAMIC VALUE RESOLUTION                                */
  /* ====================================================== */

  private buildDynamicValues(
    fo: FacilityOfferLetterParamsDto,
    trm: CreditTermParamsDto,
    covenants: OfferLetterCovenant[],
    outcomes: string[],
  ) {
    const rows = Array.isArray(trm.crdtTrmParamsLst) ? trm.crdtTrmParamsLst : [];

    // Term rows save `typ` from FACILITY_TYPE (group 13); older proposals still carry
    // REPATMENTS_TERMS (group 34) codes, so check both groups' labels.
    const isIslamic = (typ: number) =>
      [FACILITY_TYPE_GROUP, REPATMENTS_TERMS_GROUP].some((group) =>
        /islamic|mudarab/i.test(this.resolveLabel(group, typ)),
      );

    const termFinanceRows = rows.filter((row) => !isIslamic(Number(row.typ)));
    const islamicFinanceRows = rows.filter((row) => isIslamic(Number(row.typ)));

    const sumAmount = (list: typeof rows, key: 'dsbmntAmount' | 'rnwAblAmnt' | 'prmAmnt') =>
      list.reduce((total, row) => total + (Number((row as any)[key]) || 0), 0);

    const termFinanceAmnt = sumAmount(termFinanceRows, 'dsbmntAmount');
    const islamicFinanceAmnt = sumAmount(islamicFinanceRows, 'dsbmntAmount');
    const renewableEnergyAmnt = sumAmount(rows.filter((row) => Number(row.rnwAblFlg) === 1), 'rnwAblAmnt');
    const primeAmnt = sumAmount(rows.filter((row) => Number(row.prmFlg) === 1), 'prmAmnt');

    const termFinanceSpread = termFinanceRows[0]?.sprdRt;
    const islamicFinanceSpread = islamicFinanceRows[0]?.sprdRt;

    const tenorMonths = this.computeTenorMonths(trm);
    const availabilityMonths = this.computeAvailabilityMonths(rows);

    const letterDate = new Date();
    // Both counted from today, today included: acceptance 15 calendar days,
    // execution 30 calendar days (28 Oct → 11 Nov, 26 Nov).
    const acceptanceDeadline = new Date(letterDate.getTime());
    acceptanceDeadline.setDate(acceptanceDeadline.getDate() + 14);
    const executionDeadline = new Date(letterDate.getTime());
    executionDeadline.setDate(executionDeadline.getDate() + 29);

    return {
      brwrNm: fo.brwrNm?.trim() || '-',
      ceoNm: fo.ceoNm?.trim() || '-',
      ctyNm: fo.ctyNm?.trim() || '-',
      letterDate: this.formatDate(letterDate.toISOString()),
      ttlAmntWithWords: this.formatAmountWithWords(fo.ttlAmnt, fo.ttlAmntWrd),
      // Same term rows as the Term Finance / Islamic lines below, so the total always equals their sum.
      ttlAmntPlain: this.formatAmount(rows.length > 0 ? termFinanceAmnt + islamicFinanceAmnt : fo.ttlAmnt),
      scrtyAmnt: this.formatAmount(fo.scrtyAmnt),
      acceptanceDeadline: this.formatDate(acceptanceDeadline.toISOString()),
      executionDeadline: this.formatDate(executionDeadline.toISOString()),

      maxExposure: this.formatAmount(trm.prpsdExposure),
      termFinanceAmnt: this.formatAmount(termFinanceAmnt),
      islamicFinanceAmnt: this.formatAmount(islamicFinanceAmnt),
      renewableEnergyAmnt: this.formatAmount(renewableEnergyAmnt),
      primeAmnt: this.formatAmount(primeAmnt),
      availabilityMonths: availabilityMonths != null ? String(availabilityMonths) : '-',
      tenorMonths: tenorMonths != null ? String(tenorMonths) : '-',
      tenorLines: this.buildTenorLines(fo, isIslamic),
      gracePeriodMonths: trm.grcPrd != null ? String(trm.grcPrd) : '-',
      termFinanceSpread: termFinanceSpread != null ? String(termFinanceSpread) : '-',
      islamicFinanceSpread: islamicFinanceSpread != null ? String(islamicFinanceSpread) : '-',
      kiborResetMonths: this.resolveLabelOrRaw(KIBOR_RESET_PERIOD_GROUP, rows[0]?.kbrRstPrd),
      // Picklist labels read like "Quarterly (3 months)" — the pricing lines only show the number.
      kiborResetPeriodMonths: this.extractMonths(
        this.resolveLabelOrRaw(KIBOR_RESET_PERIOD_GROUP, rows[0]?.kbrRstPrd),
      ),
      // Picklist labels carry the period, e.g. "Biannually (6 months)" — only the frequency word is shown.
      repaymentFrequencyLabel: this.resolveLabelOrRaw(DISBURSEMENT_FREQUENCY_GROUP, trm.rpymntFrqncy)
        .replace(/\s*\(.*?\)\s*/g, ' ')
        .trim(),
      noOfInstlmnt: trm.noOfInstlmnt != null ? String(trm.noOfInstlmnt) : '-',
      inArrears: Number(trm.arrearFlg) === 1,
      covenants: Array.isArray(covenants) ? covenants : [],
      outcomes: Array.isArray(outcomes) ? outcomes : [],
    };
  }

  /**
   * Tenor shows fixed names: Islamic facility types → "Islamic Finance",
   * every other type (Conventional, NPL, …) → "Term Finance". Rows that
   * collapse into the same name show the longest repayment period.
   */
  private buildTenorLines(
    fo: FacilityOfferLetterParamsDto,
    isIslamic: (typ: number) => boolean,
  ): Array<{ name: string; months: string }> {
    const monthsByName = new Map<string, number | null>();

    (Array.isArray(fo.trmRpymntLst) ? fo.trmRpymntLst : []).forEach((row) => {
      const name = isIslamic(Number(row.typ)) ? 'Islamic Finance' : 'Term Finance';
      const months = row.rpymntMnths != null ? Number(row.rpymntMnths) : null;
      const current = monthsByName.get(name) ?? null;

      monthsByName.set(name, months != null && (current == null || months > current) ? months : current);
    });

    return ['Term Finance', 'Islamic Finance']
      .filter((name) => monthsByName.has(name))
      .map((name) => {
        const months = monthsByName.get(name);
        return { name, months: months != null ? String(months) : '-' };
      });
  }

  /** "Quarterly (3 months)" → "3"; labels without a month count are returned unchanged. */
  private extractMonths(label: string): string {
    const match = /(\d+)\s*months?/i.exec(label);
    return match ? match[1] : label;
  }

  private resolveLabel(group: number, value: number): string {
    if (value == null || !Number.isFinite(value)) return '';

    const option = this.pickListService
      .getOptionsSync(group)
      .find((item) => Number(item.value) === Number(value));

    return option?.label ?? '';
  }

  /** Resolves via picklist; falls back to the raw numeric value when no match exists. */
  private resolveLabelOrRaw(group: number, value: number | null | undefined): string {
    if (value == null) return '-';

    const label = this.resolveLabel(group, Number(value));
    return label || String(value);
  }

  private computeTenorMonths(trm: CreditTermParamsDto): number | null {
    if (trm.noOfInstlmnt == null) return null;

    const frequencyLabel = this.resolveLabel(DISBURSEMENT_FREQUENCY_GROUP, Number(trm.rpymntFrqncy)).toLowerCase();

    let intervalMonths = 3; // default: quarterly, matching the reference document's own default wording
    if (frequencyLabel.includes('month')) intervalMonths = 1;
    else if (frequencyLabel.includes('quarter')) intervalMonths = 3;
    else if (frequencyLabel.includes('half') || frequencyLabel.includes('semi')) intervalMonths = 6;
    else if (frequencyLabel.includes('annual') || frequencyLabel.includes('year')) intervalMonths = 12;

    return Number(trm.noOfInstlmnt) * intervalMonths;
  }

  private computeAvailabilityMonths(rows: CreditTermParamsDto['crdtTrmParamsLst']): number | null {
    const starts = rows.map((row) => this.parseDate(row.dsbStrtDt)).filter(Boolean) as Date[];
    const ends = rows.map((row) => this.parseDate(row.dsbEndDt)).filter(Boolean) as Date[];

    if (starts.length === 0 || ends.length === 0) return null;

    const earliestStart = new Date(Math.min(...starts.map((date) => date.getTime())));
    const latestEnd = new Date(Math.max(...ends.map((date) => date.getTime())));

    const months =
      (latestEnd.getFullYear() - earliestStart.getFullYear()) * 12 +
      (latestEnd.getMonth() - earliestStart.getMonth());

    return Math.max(0, months);
  }

  private addWorkingDays(start: Date, workingDays: number): Date {
    const result = new Date(start.getTime());
    let remaining = workingDays;

    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const dayOfWeek = result.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        remaining -= 1;
      }
    }

    return result;
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatDate(value: string | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) return '-';

    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  private formatAmount(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) return '-';
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  private formatAmountWithWords(amount: number | null | undefined, words: string | null | undefined): string {
    const formattedAmount = this.formatAmount(amount);
    const trimmedWords = words?.trim();

    if (formattedAmount === '-' && !trimmedWords) return '-';
    if (!trimmedWords) return formattedAmount;

    return `${formattedAmount}/- (${trimmedWords})`;
  }

  /** Lower-case roman numeral (1 → "i", 4 → "iv") for term sheet list markers. */
  private toRoman(value: number): string {
    const numerals: Array<[number, string]> = [
      [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
      [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
    ];

    let remaining = value;
    let result = '';
    for (const [amount, numeral] of numerals) {
      while (remaining >= amount) {
        result += numeral;
        remaining -= amount;
      }
    }
    return result;
  }

  private safeFileName(fileName: string): string {
    return (
      String(fileName ?? 'Facility-Offer-Letter')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'Facility-Offer-Letter'
    );
  }

  /* ====================================================== */
  /* OFFER LETTER BODY (pages 1-2)                          */
  /* ====================================================== */

  private buildOfferLetterBody(v: ReturnType<typeof this.buildDynamicValues>): Paragraph[] {
    const paragraphs: Paragraph[] = [];

    paragraphs.push(
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        spacing: { after: 200 },
        children: [new TextRun({ text: v.letterDate, size: FONT_SIZE, color: BLACK })],
      }),
    );

    [
      `Mr. ${v.ceoNm},`,
      'Chief Executive Officer,',
      `${v.brwrNm},`,
      `${v.ctyNm}.`,
    ].forEach((line) => {
      paragraphs.push(
        new Paragraph({
          spacing: { after: 20, line: 240 },
          children: [new TextRun({ text: line, size: FONT_SIZE, color: BLACK })],
        }),
      );
    });

    paragraphs.push(this.bodyParagraph(''));

    paragraphs.push(
      new Paragraph({
        spacing: { after: 120, line: 240 },
        children: [
          new TextRun({
            text: 'Re: Offer of Financing Facility from Pakistan Microfinance Investment Company Ltd. ("PMIC")',
            bold: true,
            underline: {},
            size: FONT_SIZE,
            color: BLACK,
          }),
        ],
      }),
    );

    paragraphs.push(this.bodyParagraph(`Dear Mr. ${v.ceoNm},`));

    paragraphs.push(
      this.bodyParagraphMixed(
        `In response to the funding application submitted by ${v.brwrNm} on ____________. PMIC is pleased to set out herein the terms and conditions of the proposed financing facility of PKR ${v.ttlAmntWithWords} offered by PMIC to ${v.brwrNm} (the "Financing Facility"). The Financing Facility shall be arranged on the basis of terms and conditions set out in the attached term sheet (the "Indicative Term Sheet"). This offer letter and attached Indicative Term Sheet are being collectively referred to herein as the ("Proposal Documents").`,
        ['Financing Facility', 'Indicative Term Sheet', 'Proposal Documents'],
      ),
    );

    paragraphs.push(this.heading('Basis of Offer'));
    paragraphs.push(
      this.bodyParagraph(
        'The terms set forth in the Proposal Documents are intended for discussion purposes to set out broad parameters only and are subject to the final expression of the terms of the Financing Facility, as set forth in definitive agreements. The Proposal Documents do not in any way constitute a commitment or underwriting by PMIC to lend by themselves or procure lending through third parties.',
      ),
    );

    paragraphs.push(this.heading('Confidentiality'));
    paragraphs.push(
      this.bodyParagraph(
        'By accepting delivery of these Proposal Documents, you agree that these Proposal Documents are for your confidential use only and that neither their existence nor the terms hereof will be disclosed by you to any person other than your officers, directors, employees, accountants, legal counsel and other advisors, and then only on a ‘need to know’ basis in connection with the transactions contemplated hereby and on a confidential basis (except that, notwithstanding the foregoing, you may make such public disclosures as you are required by law). The Proposal Documents and their contents are proprietary information and products of PMIC and may not be reproduced or otherwise disseminated in whole or in part without our written consent. Your obligations hereunder with respect to confidentiality shall survive the expiration or termination of the Proposal Documents.',
      ),
    );

    paragraphs.push(this.heading('Material Adverse Change'));
    paragraphs.push(
      this.bodyParagraph(
        'As detailed in the Indicative Term Sheet, PMIC reserves the unilateral right to decline arranging the Financing Facility, or to renegotiate the terms thereof, in case of any Material Adverse Change (as defined in the Indicative Term Sheet).',
      ),
    );

    paragraphs.push(this.heading('Validity', true));
    paragraphs.push(
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        keepLines: true,
        spacing: { after: 100, line: 240 },
        children: [
          new TextRun({
            text: `If the Proposal Documents are satisfactory to you, please indicate your acceptance in writing to PMIC on or before ${v.acceptanceDeadline}, after which the Proposal Documents (if not accepted prior there to) will expire, unless otherwise mutually agreed between PMIC and ${v.brwrNm} in writing. The Finance Documents must be executed in accordance with the terms thereof on or before ${v.executionDeadline}, after which the Proposal Documents will expire unless otherwise mutually agreed between PMIC and ${v.brwrNm} in writing.`,
            size: FONT_SIZE,
            color: BLACK,
          }),
        ],
      }),
    );

    paragraphs.push(
      this.bodyParagraph(
        'The Proposal Documents shall be governed by and construed in accordance with the laws of the Islamic Republic of Pakistan.',
      ),
    );

    paragraphs.push(
      this.bodyParagraph(
        'We look forward to working with you to a successful conclusion of the Financing Facility. Should you have any queries please do not hesitate to contact the undersigned.',
      ),
    );

    paragraphs.push(...this.buildSignatureBlock(v));

    return paragraphs;
  }

  private buildSignatureBlock(v: ReturnType<typeof this.buildDynamicValues>): Paragraph[] {
    // Blank line the signatory signs on, with empty lines above for room to sign.
    const SIGNATURE_LINE = '______________________________';

    const lines: Array<[string, string]> = [
      ['Offered for and on behalf of PMIC:', 'Witness:'],
      ['', ''],
      ['', ''],
      [SIGNATURE_LINE, SIGNATURE_LINE],
      ['Mr. Yasir Ashfaq', 'Mr. Saqib Siddiqui'],
      ['Chief Executive Officer', 'Chief Operating Officer'],
      ['Phone: 051 848 7820', 'Address: 21st Floor, Ufone Tower, Blue Area,'],
      ['Fax: +92 51 84 87 864', 'Islamabad, Pakistan'],
      ['Email: Yasir.ashfaq@pmic.pk', 'CNIC: 35201-1374816-5'],
      ['', ''],
      [`Accepted for and on behalf of ${v.brwrNm}:`, 'Witness:'],
      ['', ''],
      ['', ''],
      [SIGNATURE_LINE, SIGNATURE_LINE],
      [`Mr. ${v.ceoNm}`, 'Name:'],
      ['Chief Executive Officer', 'Designation:'],
      ['CNIC:', 'CNIC:'],
      ['Phone:', 'Phone:'],
      ['Email:', 'Email:'],
      ['', ''],
      ['Enclosures:', ''],
      ['Indicative Term Sheet', ''],
    ];

    return lines.map(
      ([left, right]) =>
        new Paragraph({
          tabStops: [{ type: TabStopType.LEFT, position: 5040 }],
          spacing: { after: 20, line: 240 },
          children: [
            new TextRun({ text: left, size: FONT_SIZE, color: BLACK }),
            new TextRun({ text: '\t' + right, size: FONT_SIZE, color: BLACK }),
          ],
        }),
    );
  }

  /* ====================================================== */
  /* INDICATIVE TERM SHEET (pages 3-7)                       */
  /* ====================================================== */

  private buildTermSheet(v: ReturnType<typeof this.buildDynamicValues>): Array<Paragraph | Table> {
    const output: Array<Paragraph | Table> = [];

    output.push(
      new Paragraph({
        pageBreakBefore: true,
        alignment: AlignmentType.CENTER,
        spacing: { after: 150, line: 240 },
        children: [
          new TextRun({ text: 'INDICATIVE TERM SHEET', bold: true, size: FONT_SIZE, color: BLACK }),
        ],
      }),
    );

    const rows = this.termSheetRows(v);

    const tableRows = rows.map(
      (row, index) =>
        new TableRow({
          children: [
            this.cell(String(index + 1) + ')', { widthDxa: TERM_SHEET_COLUMN_WIDTHS[0], bold: true }),
            this.cell(row.label, { widthDxa: TERM_SHEET_COLUMN_WIDTHS[1], bold: true }),
            new TableCell({
              width: { size: TERM_SHEET_COLUMN_WIDTHS[2], type: WidthType.DXA },
              verticalAlign: VerticalAlign.TOP,
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              children: row.content,
            }),
          ],
        }),
    );

    output.push(
      new Table({
        width: {
          size: TERM_SHEET_COLUMN_WIDTHS.reduce((total, width) => total + width, 0),
          type: WidthType.DXA,
        },
        columnWidths: TERM_SHEET_COLUMN_WIDTHS,
        layout: TableLayoutType.FIXED,
        borders: TABLE_BORDERS,
        rows: tableRows,
      }),
    );

    return output;
  }

  private termSheetRows(
    v: ReturnType<typeof this.buildDynamicValues>,
  ): Array<{ label: string; content: Paragraph[] }> {
    return [
      {
        label: 'Borrower:',
        content: [this.cellParagraph(`Borrower’s Name ("${v.brwrNm}")`)],
      },
      {
        label: 'Lender:',
        content: [this.cellParagraph('Pakistan Microfinance Investment Company Limited ("PMIC")')],
      },
      {
        label: 'Maximum Exposure',
        content: [
          this.cellParagraph('PMIC maximum exposure will not exceed:'),
          this.cellParagraphMixed(
            `i. PKR ${v.maxExposure} during the Tenor of the Financing Facility (the "Maximum Exposure").`,
            ['Maximum Exposure'],
          ),
          this.cellParagraph('The disbursement plan would be finalized and agreed in the Finance Documents.'),
        ],
      },
      {
        label: 'Financing Facility:',
        content: [
          this.cellParagraph(
            `The total financing facility (disbursement amount) of PKR ${v.ttlAmntPlain} as per the following details:`,
          ),
          this.cellParagraphMixed(`Term Finance Facility: PKR ${v.termFinanceAmnt}/-`, ['Term Finance Facility: ']),
          this.cellParagraphMixed(
            `Islamic Financing (Mudarabah): PKR ${v.islamicFinanceAmnt}/-`,
            ['Islamic Financing '],
          ),
        ],
      },
      {
        label: 'Renewable Energy:',
        content: [
          this.cellParagraphMixed(
            `PRIME: An amount of PKR ${v.primeAmnt}/- from the proposed facility (TF) will be allocated for financing renewable energy.`,
            ['PRIME:'],
          ),
        ],
      },
      {
        label: 'Grant Support Under MF Plus:',
        content: [
          this.cellParagraphMixed(
            `Grant Support: An amount of PKR ${v.renewableEnergyAmnt} to ${v.brwrNm} for procurement of quality certified solar systems under the PRIME Program.`,
            ['Grant Support:'],
          ),
        ],
      },
      {
        label: 'Subsidy Scheme Under PRIME Program:',
        content: [
          this.cellParagraphMixed(
            `End-User Subsidy: It shall be passed on to end clients directly through ${v.brwrNm} and shall be provided based on the subsidy rates defined in Term Finance Agreement.`,
            ['End-User Subsidy: '],
          ),
          this.cellParagraphMixed(
            `Interest-Rate Subsidy: It will be provided to ${v.brwrNm} based on the financing deployed under this initiative and will be based on the results-based framework;`,
            ['Interest-Rate Subsidy: '],
          ),
          this.cellListItem(
            'i.',
            `PMIC will offer ${v.brwrNm} an interest rate subsidy as a one-time fee for the financing made under PRIME program`,
          ),
          this.cellListItem(
            'ii.',
            `Based on the targets submitted and agreements signed with ${v.brwrNm}, PMIC will offer 3% subsidy on financing deployed during the preceding quarter`,
          ),
          this.cellListItem(
            'iii.',
            'PMIC shall provide an additional subsidy of 0.5 % upon 100% achievement of annual financing targets within the agreed time period.',
          ),
          this.cellListItem(
            'iv.',
            `A further 0.5% subsidy shall be offered to ${v.brwrNm} for completing the annual financing targets at least 30 days before the completion of the agreed financing period.`,
          ),
        ],
      },
      {
        label: 'Facility Effective Date:',
        content: [
          this.cellParagraph(
            'The Facility Effective Date ("FED") is the date of first drawdown on which the Conditions Precedent to the Financing Facility as specified in the Finance Documents, are satisfied, and the Financing Facility becomes available for use.',
          ),
          this.cellParagraph(
            'If the FED is not achieved within 30 days of the execution of the Finance Documents, the Financing Facility shall expire, unless such expiry date is extended by PMIC in writing.',
          ),
        ],
      },
      {
        label: 'Availability Period',
        content: [
          this.cellParagraph(
            `The Availability Period shall be up to ${v.availabilityMonths} months, commencing from the FED during which the Financing Facility shall be available for use. Drawdown under the Financing Facility may be allowed in tranches.`,
          ),
          this.cellParagraph('Any sums un-drawn under the Financing Facility on the expiry of the Availability Period shall stand cancelled.'),
        ],
      },
      {
        label: 'Tenor',
        content: v.tenorLines.length
          ? v.tenorLines.map((line) =>
              this.cellParagraphMixed(
                `${line.name}: ${line.months} Months door to door for each installment.`,
                [`${line.name}:`],
              ),
            )
          : [
              this.cellParagraphMixed(
                `Term Finance: ${v.tenorMonths} Months door to door for each installment.`,
                ['Term Finance:'],
              ),
              this.cellParagraphMixed(
                `Islamic Finance: ${v.tenorMonths} Months door to door for each installment.`,
                ['Islamic Finance: '],
              ),
            ],
      },
      {
        label: 'Grace Period',
        content: [
          this.cellParagraphMixed(
            `Term Finance/Islamic Finance: The grace period shall be ${v.gracePeriodMonths} months from the FED for each tranche, during which the markup on the outstanding Financing Facility shall be serviced quarterly, but principal payments shall not become due ("Grace Period")`,
            ['Term Finance/Islamic Finance:', 'Grace Period'],
          ),
        ],
      },
      {
        label: 'Drawdowns',
        content: [
          this.cellParagraph(
            `All drawdowns will be as per the agreed terms of the Finance Documents, subject to maximum exposure limits and satisfactory performance of the terms of the Finance Documents by ${v.brwrNm}.`,
          ),
        ],
      },
      {
        label: 'Pricing',
        content: [
          this.cellParagraphMixed(
            `Term Finance: Base Rate ${v.kiborResetPeriodMonths} Month Kibor + ${v.termFinanceSpread}% per annum.`,
            ['Term Finance:'],
          ),
          this.cellParagraphMixed(
            `Islamic Finance (Mudarabah): ${v.kiborResetPeriodMonths} Month Kibor + ${v.islamicFinanceSpread}%. The profit and loss participation ratios, provisional profit, and the actual frequency will be determined through mutual agreement`,
            ['Islamic Finance (Mudarabah): '],
          ),
          this.cellParagraphMixed(
            `${v.kiborResetPeriodMonths} Month KIBOR (ask side) as published by Reuters page KIBOR at 11:30 am Pakistan Standard Time or as published by the Financial Markets Association of Pakistan in case the Reuters page is unavailable shall be the "Base Rate". ${
              v.kiborResetPeriodMonths === '3'
                ? 'The Base Rate shall be set on the first working day of March, June, September and December for each respective quarter of every calendar year.'
                : 'The Base Rate shall be set on the first working day of January and July for the first and second half of the year respectively of every calendar year.'
            }`,
            ['Base Rate'],
          ),
        ],
      },
      {
        label: 'Principal Payments',
        content: [
          this.cellParagraph(
            `Repayment for each tranche of disbursement will be made in ${v.noOfInstlmnt} ${v.repaymentFrequencyLabel} installments. The instalments will commence after the end of grace period${v.inArrears ? ' in arrears' : ''}, and continue ${v.repaymentFrequencyLabel.toLowerCase()}, thereafter. As and when the Borrower requests multiple drawdowns under the Financing Facility, PMIC shall update the repayment schedule accordingly.`,
          ),
        ],
      },
      {
        label: 'Markup Payments',
        content: [this.cellParagraph(`${v.repaymentFrequencyLabel}, accruing from the FED`)],
      },
      {
        label: 'Conditions Precedent',
        content: [
          this.cellParagraphMixed(
            `The Financing Facility to be advanced by PMIC to ${v.brwrNm} will be on the terms of and subject to the conditions set forth in binding finance documents and agreements, each in form and substance acceptable to PMIC (the "Finance Documents").`,
            ['Finance Documents'],
          ),
          this.cellParagraphMixed(
            'The conditions to FED to be mutually agreed upon in the Finance Documents shall include, but not be limited to, the following (the "Conditions Precedent"), which shall be fulfilled to the satisfaction of PMIC:',
            ['Conditions Precedent'],
          ),
          this.cellParagraph(
            `i. All authorizations required by ${v.brwrNm} in connection with the execution, delivery, validity or enforceability of the Finance Documents (including but not limited to Board Resolution, Shareholders resolution (if applicable), etc.), the granting of authority to an authorized officer or authorized officers to execute and deliver the Finance Documents or any other document or notice in connection therewith, and the performance by ${v.brwrNm} of all its obligations under the Finance Documents.`,
          ),
          this.cellParagraphMixed(
            'ii. Perfection of the Security (as required) by way of registration of charges with the Securities and Exchange Commission of Pakistan ("SECP").',
            ['SECP'],
          ),
          this.cellParagraph(
            `iii. Confirmation from ${v.brwrNm} that all required insurances for its business are in full force and effect.`,
          ),
          this.cellParagraph(
            `iv. Receipt of a clean legal opinion from the legal counsel of ${v.brwrNm}, confirming inter alia the validity, enforceability and binding effect of the obligations of ${v.brwrNm} under the Finance Documents (including related security documents), in form and substance acceptable to PMIC.`,
          ),
          this.cellParagraph('v. Appointment of auditors as per list of SBP approved auditors.'),
          this.cellParagraph(
            `vi. Opinion from the auditors of ${v.brwrNm} confirming that ${v.brwrNm} is not in breach of the terms of any indebtedness obtained by ${v.brwrNm}.`,
          ),
          this.cellParagraph(`vii. ${v.brwrNm} shall provide PMIC with:`),
          this.cellParagraph(
            `a. One certified true copy of its Certificate of Registration duly certified/attested by the Registering Authority and authorized officer of ${v.brwrNm}.`,
          ),
          this.cellParagraph(
            `b. One certified true copy of the license issued from the competent authorities of Pakistan to ${v.brwrNm} to provide microfinance services duly certified/ attested by the Registering Authority and authorized officer of ${v.brwrNm}.`,
          ),
          this.cellParagraph(
            `c. One certified true copy of the Constitution and Byelaws of ${v.brwrNm} duly certified/attested by the Registering Authority and authorized officer of ${v.brwrNm}.`,
          ),
          this.cellParagraph(
            `d. Resolutions of the board of directors/ members (as required) of ${v.brwrNm} covering inter alia the following: (i) acceptance of the terms of the Proposal Documents and the Finance Documents; (ii) Approve the execution, delivery and performance of each term documents (iii) Designation of an authorized signatory to enter into the Term Documents on behalf of ${v.brwrNm}; and (iv) appointment of a coordinator for interacting with PMIC.`,
          ),
          this.cellParagraph('e. Copies of identity documents i.e CNIC or Passport/ valid travel documents (where applicable) of Board members'),
        ],
      },
      {
        label: 'Covenants',
        content: [
          this.cellParagraph(
            `${v.brwrNm} is obligated to comply, at each drawdown and thereafter throughout the Tenor of the Financing Facility, with covenants (positive and negative) as set out in the Finance Documents, including, but not limited to:`,
          ),
          this.cellParagraph('Financial and Other Covenants:', true),
          this.cellParagraph(
            'Maintenance of the following ratios (the thresholds for which and method of calculation shall be defined in the Finance Documents):',
          ),
          ...(v.covenants.length
            ? v.covenants.map((covenant, index) =>
                this.cellListItem(
                  `${index + 1}.`,
                  covenant.answer ? `${covenant.question}: ${covenant.answer}` : covenant.question,
                ),
              )
            : [this.cellParagraph('1. Add Covenants'), this.cellParagraph('2.')]),
        ],
      },
      {
        label: 'Outcomes',
        content: v.outcomes.length
          ? v.outcomes.map((outcome, index) => this.cellListItem(`${this.toRoman(index + 1)}.`, outcome))
          : [this.cellParagraph('i. Add Outcomes')],
      },
      {
        label: 'Security',
        content: [
          this.cellParagraphMixed('The security for the Financing Facility shall consist of (the "Security"):', ['Security']),
          this.cellParagraphMixed(
            `i. Term Finance: A hypothecation charge, at least on a first pari passu basis with other lenders of ${v.brwrNm}, on all the present and future current assets of ${v.brwrNm} up to PKR ${v.scrtyAmnt}. The charge shall be registered with the SECP within 30 days of the signing of the Finance Documents, initially as ranking charge. However, the charge will be upgraded as first pari passu with existing lenders within 90 days from the date of first draw down.`,
            ['Term Finance:'],
          ),
          this.cellParagraphMixed(
            `ii. Islamic Finance: A hypothecation charge, at least on a first pari passu basis with other lenders of ${v.brwrNm}, on all the on all the present and future current assets (excluding interest amount receivables) of ${v.brwrNm} up to PKR ${v.scrtyAmnt}. The charge shall be registered with the SECP within 30 days of the signing of the Finance Documents, initially as ranking charge. However, the charge will be upgraded as first pari passu with existing lenders within 90 days from the date of first draw down.`,
            ['Islamic Finance:'],
          ),
          this.cellParagraph('*Charge for the two modes of financing shall be separately registered.'),
          this.cellParagraph(
            `iii. A Demand Promissory Note signed by the BoD/ authorized representative of ${v.brwrNm}, executed on stamp paper of the requisite amount as per applicable laws of Pakistan.`,
          ),
        ],
      },
      {
        label: 'Events of Default',
        content: [
          this.cellParagraph(
            'PMIC may terminate its commitments under the Finance Documents and declare all obligations of the Borrower to be immediately due and payable and enforce the Security if any ‘Event of Default’ occurs (subject to relevant cure periods). The Events of Default will be usual and customary for a transaction of this type, including, without limitation, the following:',
          ),
          this.cellParagraph('i. Failure to make timely payments under the Finance Documents.'),
          this.cellParagraph('ii. The Borrower breaches any covenant, term or condition of the Finance Documents.'),
          this.cellParagraph('iii. Illegality, invalidity or unenforceability of the Finance Documents or the Finance Documents otherwise cease to be in full force and effect.'),
          this.cellParagraph('iv. Invalidity of the Security or material diminution in the value of any Security.'),
          this.cellParagraph(
            'v. Failure to comply with the Non-Banking Finance Companies (Establishment and Regulation) Rules, 2003 (as amended or reenacted from time to time), the Non-Banking Finance Companies and Notified Entities Regulations, 2008 (as amended or reenacted from time to time) and all applicable laws of Pakistan including State Bank of Pakistan Regulations/Guidelines as and when applicable.',
          ),
          this.cellParagraph('vi. Any event or series of events occurs which, in the reasonable opinion of PMIC, has or is likely to have or result in a material adverse effect.'),
          this.cellParagraph('vii. Other Events of Default as determined and required after due diligence.'),
          this.cellParagraph('iv. Certain Events of Default will be subject to an appropriate remedy period (i.e., cure period). Exceptions and qualifications (including those relating to materiality and reasonableness) will be included in the Finance Documents.'),
        ],
      },
      {
        label: 'Material Adverse Change',
        content: [
          this.cellParagraphMixed(
            'PMIC shall have the right to renegotiate the terms of the Financing Facility, or else, cancel the offer made under the Proposal Documents if, in the reasonable judgement of PMIC, any of the following occurs (each a "Material Adverse Change"):',
            ['Material Adverse Change'],
          ),
          this.cellParagraph(
            `i. There is a material change in any of the business, financial conditions, operations, performance, properties or prospects of ${v.brwrNm}; or`,
          ),
          this.cellParagraph(
            'ii. There is a change of circumstance or condition (including the continuation of any existing condition) in the commercial bank, loan syndication, financial or capital markets.',
          ),
          this.cellParagraph('For references of Material Adverse Change post-FED, the definitions in the Finance Documents shall be used as a standard.'),
        ],
      },
      {
        label: 'Indemnification',
        content: [
          this.cellParagraphMixed(
            `${v.brwrNm} hereby indemnifies and agrees to hold harmless PMIC and each of its affiliates, their respective officers, directors, employees, agents, advisors & representatives (each an "Indemnified Party") from and against any and all claims, damages, losses, liabilities, costs & expenses (hereinafter collectively referred as "Claim"), joint and several, that may be made against, incurred by or awarded against any Indemnified Party, in each case arising out of or in connection with or relating to performance of the Indemnified Party under the Proposal Documents, except to the extent such Claim(s) have resulted from such Indemnified Party’s willful misconduct.`,
            ['Indemnified Party', 'Claim'],
          ),
        ],
      },
      {
        label: 'Taxes, Excise Duties, Government Levies',
        content: [
          this.cellParagraph(
            `${v.brwrNm} shall pay all excise, levies, stamp duties, other duties or surcharges required to be paid in connection with the Financing Facility. All fees due under these Proposal Documents shall be grossed up for Federal Excise Duty, at the rate prevalent at the time of such payment.`,
          ),
        ],
      },
      {
        label: 'No Conditional Payments',
        content: [
          this.cellParagraph(
            `Payments by ${v.brwrNm} of any dues payable to PMIC shall not be subject to counterclaim or setoff for, or be otherwise affected by, any claim or dispute relating to any matter whatsoever and all such payments shall be made in immediately available free and clear funds without deduction for or on account of any present or future taxes, charges, deductions or withholdings, unless required by law.`,
          ),
        ],
      },
      {
        label: 'Governing Law',
        content: [
          this.cellParagraph(
            'The Proposal Documents shall be governed by and construed in accordance with the laws of the Islamic Republic of Pakistan.',
          ),
        ],
      },
    ];
  }

  /* ====================================================== */
  /* PARAGRAPH / CELL HELPERS                               */
  /* ====================================================== */

  private heading(text: string, pageBreakBefore = false): Paragraph {
    return new Paragraph({
      heading: HeadingLevel.HEADING_3,
      keepNext: true,
      pageBreakBefore,
      spacing: { before: 120, after: 60, line: 240 },
      children: [new TextRun({ text, bold: true, size: FONT_SIZE, color: BLACK })],
    });
  }

  private bodyParagraph(text: string): Paragraph {
    return new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 100, line: 240 },
      children: [new TextRun({ text, size: FONT_SIZE, color: BLACK })],
    });
  }

  private cellParagraph(text: string, bold = false): Paragraph {
    return new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 60, line: 240 },
      children: [new TextRun({ text, bold, size: TABLE_FONT_SIZE, color: BLACK })],
    });
  }

  /**
   * Numbered list item inside a table cell: the marker sits in a hanging
   * indent so wrapped lines align under the text, not under the marker.
   */
  private cellListItem(marker: string, text: string): Paragraph {
    const markerWidth = 360;
    const left = 120 + markerWidth;

    return new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 60, line: 240 },
      indent: { left, hanging: markerWidth },
      tabStops: [{ type: TabStopType.LEFT, position: left }],
      children: [new TextRun({ text: `${marker}\t${text}`, size: TABLE_FONT_SIZE, color: BLACK })],
    });
  }

  /** Same as `bodyParagraph`, but only the given phrases render bold — the rest stays regular weight. */
  private bodyParagraphMixed(text: string, boldPhrases: string[]): Paragraph {
    return new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 100, line: 240 },
      children: this.splitBoldSegments(text, boldPhrases).map(
        (segment) =>
          new TextRun({ text: segment.text, bold: segment.bold, size: FONT_SIZE, color: BLACK }),
      ),
    });
  }

  /** Same as `cellParagraph`, but only the given phrases render bold — the rest stays regular weight. */
  private cellParagraphMixed(text: string, boldPhrases: string[]): Paragraph {
    return new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 60, line: 240 },
      children: this.splitBoldSegments(text, boldPhrases).map(
        (segment) =>
          new TextRun({ text: segment.text, bold: segment.bold, size: TABLE_FONT_SIZE, color: BLACK }),
      ),
    });
  }

  /**
   * Splits `text` into an ordered run list, marking each `boldPhrases` entry
   * bold and leaving everything else regular weight. Phrases are matched in
   * order starting after the previous match, so the same phrase can be
   * listed more than once if it needs to be bold in more than one spot.
   */
  private splitBoldSegments(
    text: string,
    boldPhrases: string[],
  ): Array<{ text: string; bold: boolean }> {
    const segments: Array<{ text: string; bold: boolean }> = [];
    let cursor = 0;

    boldPhrases.forEach((phrase) => {
      const index = text.indexOf(phrase, cursor);
      if (index === -1) return;

      if (index > cursor) {
        segments.push({ text: text.slice(cursor, index), bold: false });
      }

      segments.push({ text: phrase, bold: true });
      cursor = index + phrase.length;
    });

    if (cursor < text.length) {
      segments.push({ text: text.slice(cursor), bold: false });
    }

    return segments;
  }

  private cell(text: string, options: { widthDxa?: number; bold?: boolean } = {}): TableCell {
    return new TableCell({
      width: options.widthDxa ? { size: options.widthDxa, type: WidthType.DXA } : undefined,
      verticalAlign: VerticalAlign.TOP,
      margins: { top: 60, bottom: 60, left: 80, right: 80 },
      children: [
        new Paragraph({
          children: [new TextRun({ text, bold: Boolean(options.bold), size: TABLE_FONT_SIZE, color: BLACK })],
        }),
      ],
    });
  }
}
